import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Terminal, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("System unlocked.");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-16 flex items-center justify-center" data-testid="login-page">
      <div className="w-full max-w-md mx-auto px-6">
        <div className="terminal">
          <div className="terminal-header">
            <span className="terminal-dot red"></span>
            <span className="terminal-dot amber"></span>
            <span className="terminal-dot green"></span>
            <span className="ml-2 uppercase">~/admin --login</span>
          </div>
          <form onSubmit={submit} className="p-8 space-y-6">
            <div>
              <Terminal className="w-6 h-6 text-volt mb-3" />
              <h1 className="font-display font-black text-2xl tracking-tight">System locked.</h1>
              <p className="text-fog-300 text-sm mt-1">Admin access required.</p>
            </div>
            <div>
              <label className="overline block mb-1">email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                data-testid="login-input-email"
              />
            </div>
            <div>
              <label className="overline block mb-1">password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  data-testid="login-input-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-fog-300 hover:text-white"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="login-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="btn-volt w-full justify-center" data-testid="login-submit-btn">
              <Lock className="w-4 h-4" /> {loading ? "unlocking…" : "UNLOCK"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
